import { useCallback, useState } from "react";

interface StreamingMessage {
  messageId: string
  content: string
  isComplete: boolean
  created: Date
}

interface StreamingStateActions {
  createPlaceholder: (messageId: string) => void
  updateStreamingContent: (messageId: string, chunk: string) => void
  completeStreaming: (messageId: string) => void
  clearStreaming: (messageId: string) => void
  clearMultiple: (messageIds: string[]) => void
}

interface StreamingStateReturn {
  streamingMessages: Map<string, StreamingMessage>
  actions: StreamingStateActions
}

/**
 * Simple hook to manage streaming message state with CRUD operations.
 * This hook has no side effects and no complex logic - just state management.
 */
export function useStreamingState(): StreamingStateReturn {
  const [streamingMessages, setStreamingMessages] = useState<Map<string, StreamingMessage>>(new Map())

  const createPlaceholder = useCallback((messageId: string) => {
    setStreamingMessages(prev => {
      if (prev.has(messageId)) {
        // Don't create duplicate
        return prev
      }
      const updated = new Map(prev)
      updated.set(messageId, {
        messageId,
        content: '',
        isComplete: false,
        created: new Date()
      })
      return updated
    })
  }, [])

  const updateStreamingContent = useCallback((messageId: string, chunk: string) => {
    setStreamingMessages(prev => {
      const updated = new Map(prev)
      const existing = updated.get(messageId)
      if (existing) {
        const rawContent = existing.content + chunk
        // Handle escaped newlines
        const newContent = rawContent.replace(/\\n/g, '\n')
        updated.set(messageId, {
          ...existing,
          content: newContent
        })
      }
      return updated
    })
  }, [])

  const completeStreaming = useCallback((messageId: string) => {
    setStreamingMessages(prev => {
      const updated = new Map(prev)
      const existing = updated.get(messageId)
      if (existing) {
        updated.set(messageId, {
          ...existing,
          isComplete: true
        })
      }
      return updated
    })
  }, [])

  const clearStreaming = useCallback((messageId: string) => {
    setStreamingMessages(prev => {
      const updated = new Map(prev)
      updated.delete(messageId)
      return updated
    })
  }, [])

  const clearMultiple = useCallback((messageIds: string[]) => {
    if (messageIds.length === 0) return

    setStreamingMessages(prev => {
      let hasChanges = false
      const updated = new Map(prev)
      
      messageIds.forEach(messageId => {
        if (updated.has(messageId)) {
          updated.delete(messageId)
          hasChanges = true
        }
      })
      
      // Only return new Map if there were actual changes
      return hasChanges ? updated : prev
    })
  }, [])

  const actions: StreamingStateActions = {
    createPlaceholder,
    updateStreamingContent,
    completeStreaming,
    clearStreaming,
    clearMultiple
  }

  return {
    streamingMessages,
    actions
  }
}